/**
 * Home — the ahwa facade.
 *
 * Hot-seat play is the primary path and never touches Convex. Online room
 * controls (and their Convex hooks) live in a separate component that only
 * mounts when a Convex deployment is configured.
 */

import type { GameMode } from "@domino/types";
import { useNavigate } from "@tanstack/react-router";
import { useMutation } from "convex/react";
import { useState } from "react";

import { api } from "../../../convex/convex/_generated/api";
import { GuideModal } from "../components/GuideModal";
import { TileFace } from "../components/tiles";
import { hasConvex } from "../lib/convex";
import { displayName, playerId, setDisplayName } from "../lib/player";
import { sounds } from "../lib/sound";

const MODES: Array<{
  id: GameMode;
  ar: string;
  title: string;
  desc: string;
  players: number;
}> = [
  {
    id: "2v2",
    ar: "زوجي",
    title: "2 v 2 Partners",
    desc: "Partners opposite, all 28 stones dealt, pass only.",
    players: 4,
  },
  {
    id: "1v1",
    ar: "فردي",
    title: "1 v 1 Duel",
    desc: "Head-to-head. Knock or draw freely.",
    players: 2,
  },
  {
    id: "3p",
    ar: "ثلاثي",
    title: "3 Players",
    desc: "Every man for himself, draw pile live.",
    players: 3,
  },
];

const TARGETS = [50, 100, 150, 200];

export default function HomePage() {
  const navigate = useNavigate();
  const [name, setName] = useState(displayName() || "");
  const [mode, setMode] = useState<GameMode>("2v2");
  const [targetScore, setTargetScore] = useState<number>(100);
  const [error, setError] = useState<string | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);

  function requireName(): string | null {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("اكتب اسمك — enter a name to sit down.");
      return null;
    }
    setError(null);
    setDisplayName(trimmed);
    return trimmed;
  }

  function handleQuickLocal() {
    const who = requireName();
    if (!who) return;
    sounds.unlock();
    sounds.ui();
    void navigate({ to: "/local", search: { mode, target: targetScore } });
  }

  return (
    <div className="home-root">
      {/* ambient ahwa dressing — the Cairo alley behind the shop */}
      <div aria-hidden>
        <div className="lobby-backdrop" />
        <div className="lobby-vignette" />
      </div>

      <div className="home-shell">
        {/* the sign */}
        <header className="home-sign">
          <button
            type="button"
            className="sign-btn"
            onClick={() => {
              sounds.ui();
              setGuideOpen(true);
            }}
            aria-label="Open the guide"
          >
            ؟
          </button>

          <div className="sign-board">
            <span className="sign-ar">دومينو</span>
            <span className="sign-en">DOMINO</span>
          </div>

          <div className="sign-tiles" aria-hidden>
            <TileFace tileId={27} size="sm" className="sign-tile sign-tile-1" />
            <TileFace tileId={25} size="sm" className="sign-tile sign-tile-2" />
            <TileFace tileId={0} size="sm" className="sign-tile sign-tile-3" />
          </div>
        </header>

        <main className="home-console">
          {/* name + table setup */}
          <section className="panel panel-setup">
            <label className="field">
              <span className="field-label">
                اسمك إيه؟ <i>your name</i>
              </span>
              <input
                className="field-input"
                placeholder="اكتب اسمك…"
                value={name}
                maxLength={18}
                autoComplete="off"
                onChange={(e) => {
                  setName(e.target.value);
                  setDisplayName(e.target.value);
                }}
              />
            </label>

            <div className="field">
              <span className="field-label">
                القعدة <i>the table</i>
              </span>
              <div className="mode-row">
                {MODES.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className={`mode-card ${mode === m.id ? "mode-active" : ""}`}
                    onClick={() => {
                      setMode(m.id);
                      sounds.ui();
                    }}
                  >
                    <span className="mode-ar">{m.ar}</span>
                    <span className="mode-title">{m.title}</span>
                    <span className="mode-desc">{m.desc}</span>
                    <span className="mode-seats">
                      {Array.from({ length: m.players }).map((_, i) => (
                        <i key={i} />
                      ))}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="field">
              <span className="field-label">
                حد الخسران <i>first to reach it loses</i>
              </span>
              <div className="target-row">
                {TARGETS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`target-chip ${targetScore === t ? "target-active" : ""}`}
                    onClick={() => {
                      setTargetScore(t);
                      sounds.ui();
                    }}
                  >
                    <span className="target-num">{t}</span>
                    <span className="target-word">خسارة</span>
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* actions */}
          <section className="panel panel-actions">
            <button type="button" className="cta-primary" onClick={handleQuickLocal}>
              <span className="cta-ar">اقعد اتلعب</span>
              <span className="cta-en">
                Play {mode === "2v2" ? "2v2" : mode === "3p" ? "3-player" : "1v1"} · pass-the-device
              </span>
              <span className="cta-glow" aria-hidden />
            </button>

            {hasConvex ? (
              <OnlinePanel mode={mode} targetScore={targetScore} />
            ) : (
              <p className="online-note">
                Online rooms are available when a Convex backend is configured — the table above is
                fully playable right now.
              </p>
            )}

            {error && <div className="home-error">{error}</div>}
          </section>
        </main>

      </div>

      <GuideModal isOpen={guideOpen} onClose={() => setGuideOpen(false)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Online rooms — isolated so Convex hooks never mount without a deployment
// ---------------------------------------------------------------------------

function OnlinePanel({ mode, targetScore }: { mode: GameMode; targetScore: number }) {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createRoom = useMutation(api.rooms.createRoom);
  const joinRoom = useMutation(api.rooms.joinRoom);

  async function handleCreate() {
    setBusy(true);
    setError(null);
    sounds.ui();
    try {
      const result = await createRoom({
        playerId: playerId(),
        displayName: displayName() || "Player",
        mode,
        targetScore,
      });
      await navigate({ to: "/room/$code", params: { code: result.code } });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create the room");
    } finally {
      setBusy(false);
    }
  }

  async function handleJoin() {
    const trimmed = code.trim().toUpperCase();
    if (trimmed.length < 4) {
      setError("Room codes are 4 characters.");
      return;
    }
    setBusy(true);
    setError(null);
    sounds.ui();
    try {
      await joinRoom({ code: trimmed, playerId: playerId(), displayName: displayName() || "Player" });
      await navigate({ to: "/room/$code", params: { code: trimmed } });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not join room");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="online-box">
      <div className="online-head">
        <span className="online-title">
          أونلاين <i>online rooms</i>
        </span>
      </div>
      <div className="online-controls">
        <button type="button" className="btn-outline" onClick={handleCreate} disabled={busy}>
          افتح طاولة · Host a table
        </button>
        <div className="join-row">
          <input
            className="field-input join-input"
            placeholder="CODE"
            value={code}
            maxLength={8}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
          />
          <button type="button" className="btn-outline btn-join" onClick={handleJoin} disabled={busy}>
            ادخل · Join
          </button>
        </div>
        {error && <div className="home-error">{error}</div>}
      </div>
    </div>
  );
}
